import { useCallback, useMemo } from "react";

import type { BackendEntityType, InvitationStatus, PaginationParamsModel } from "shared/types";

import useToast from "./useToast";

import { useWaiversStore } from "../contexts";
import useWaiversService from "../services/useWaiversApiService";

const useWaivers = () => {
	const waiversService = useWaiversService();

	const store = useWaiversStore();
	const { setState, setInitial } = useWaiversStore();

	const { showToast } = useToast();

	const methods = useMemo(() => {
		return {
			async acceptWaiver(waiverSignatureId: string, asPersonId?: string) {
				try {
					setState({ submitting: true });
					const { success } = await waiversService.acceptWaiver(waiverSignatureId, asPersonId);
					if (success) {
						showToast({
							text: "Waiver is successfully signed",
							noIcon: true
						});
					}
					return true;
				} catch (error) {
					showToast({
						text: "Can't sign a waiver",
						noIcon: true
					});
					console.log(error);
					return false;
				} finally {
					setState({ submitting: false });
				}
			},
			async listUserWaivers(params: { status?: InvitationStatus | InvitationStatus[] } & PaginationParamsModel) {
				setState({ loading: true });

				try {
					const { waiverSignatures, nextToken } = await waiversService.listUserWaivers(params);

					setState(ctx => ({
						nextToken,
						waiverSignatures: !!params?.after ? ctx.waiverSignatures : waiverSignatures
					}));

					return {
						waiverSignatures,
						nextToken
					};
				} catch (err) {
					console.error(err);
					showToast({ text: "Something went wrong", noIcon: true });
					return {
						nextToken: null,
						waiverSignatures: []
					};
				} finally {
					setState({ loading: false });
				}
			},
			async listEntityWaivers(
				params: {
					status?: InvitationStatus | InvitationStatus[];
					objectId: string;
					objectType: BackendEntityType;
				} & PaginationParamsModel
			) {
				try {
					const { waivers, nextToken } = await waiversService.listEntityWaivers(params);

					return {
						waivers,
						nextToken
					};
				} catch (err) {
					console.error(err);
					showToast({ text: "Something went wrong", noIcon: true });
					return {
						nextToken: null,
						waivers: []
					};
				}
			},
			async sendWaiverReminder(waiverSignatureId: string, guardianId: string) {
				try {
					setState({ submitting: true });
					const { success } = await waiversService.sendWaiverReminder(waiverSignatureId, guardianId);
					if (success) {
						showToast({
							text: "Waiver reminder is successfully sent",
							noIcon: true
						});
					}
					return true;
				} catch (error) {
					showToast({
						text: "Can't send a waiver reminder",
						noIcon: true
					});
					console.log(error);
					return false;
				} finally {
					setState({ submitting: false });
				}
			},
			resetStore() {
				setInitial();
			}
		};
	}, [setState, waiversService, showToast, setInitial]);

	const getData = useCallback(() => {
		return store;
	}, [store]);

	return { ...methods, getData };
};

export default useWaivers;
